/**
 * Locale registry and access.
 *
 * Adding a locale (e.g. French) is three mechanical edits, no refactor:
 *   1. `cp ro.ts fr.ts`, translate the values, and type it:
 *        import type { Dictionary } from './ro';
 *        export const fr: Dictionary = { ... };
 *      TypeScript then reports every key the translation missed.
 *   2. Register it in `locales` below.
 *   3. Render <LocaleSwitcher /> (not built — no second locale exists yet).
 *
 * Components never import `ro` directly. They call `useT()`, so they are
 * already locale-agnostic and need no changes when a locale is added.
 */

import { createContext, useContext } from 'react';
import { ro, type Dictionary } from './ro';

/** Every locale the app can render. Add new entries here. */
export const locales = {
  ro,
} satisfies Record<string, Dictionary>;

export type Locale = keyof typeof locales;

export const DEFAULT_LOCALE: Locale = 'ro';

/** Locales offered in a picker, in display order. */
export const AVAILABLE_LOCALES = Object.keys(locales) as Locale[];

export function isLocale(value: string): value is Locale {
  return Object.prototype.hasOwnProperty.call(locales, value);
}

export function getDictionary(locale: Locale): Dictionary {
  return locales[locale];
}

export interface LocaleContextValue {
  locale: Locale;
  t: Dictionary;
  setLocale: (locale: Locale) => void;
}

export const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  t: locales[DEFAULT_LOCALE],
  setLocale: () => {
    // No-op default. The real setter comes from LocaleProvider.
  },
});

/** The active dictionary. `const t = useT(); t.step1.heading` */
export function useT(): Dictionary {
  return useContext(LocaleContext).t;
}

/** The active locale plus its setter, for a future language switcher. */
export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

export type { Dictionary };
